import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { cn } from "../../utils/format";

type Variant = "primary" | "outline" | "ghost" | "dark";
type Size = "sm" | "md" | "lg";

interface BaseProps {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  className?: string;
  children?: ReactNode;
}

type AsButton = BaseProps &
  Omit<ComponentPropsWithoutRef<"button">, keyof BaseProps> & { to?: never; href?: never };
type AsLink = BaseProps & Omit<LinkProps, keyof BaseProps> & { href?: never };
type AsAnchor = BaseProps &
  Omit<ComponentPropsWithoutRef<"a">, keyof BaseProps> & { href: string; to?: never };

export type ButtonProps = AsButton | AsLink | AsAnchor;

const VARIANTS: Record<Variant, string> = {
  primary: "btn-glow bg-primary text-night hover:bg-primary-hover",
  outline: "border border-line bg-white/[0.02] text-white hover:border-primary hover:text-primary",
  ghost: "text-white hover:text-primary",
  dark: "bg-night text-white hover:bg-coal focus-visible:outline-night",
};

const SIZES: Record<Size, string> = {
  sm: "h-9 gap-1.5 px-4 text-xs",
  md: "h-11 gap-2 px-6 text-sm",
  lg: "h-[52px] gap-2.5 px-8 text-base",
};

const isLink = (props: ButtonProps): props is AsLink => "to" in props && props.to !== undefined;
const isAnchor = (props: ButtonProps): props is AsAnchor =>
  "href" in props && props.href !== undefined;

const split = <T extends BaseProps>(props: T) => {
  const { variant = "primary", size = "md", icon, iconPosition = "right", className, children, ...rest } =
    props;
  const classes = cn(
    "inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-full font-semibold",
    "transition-[background-color,color,border-color,transform] duration-200 active:scale-[0.97]",
    "disabled:pointer-events-none disabled:opacity-50",
    VARIANTS[variant],
    SIZES[size],
    className,
  );
  const content = (
    <>
      {icon && iconPosition === "left" && icon}
      {children}
      {icon && iconPosition === "right" && icon}
    </>
  );
  return { classes, content, rest };
};

/** Botão em pílula. Vira <Link> com `to`, <a> com `href`, ou <button>. */
export const Button = (props: ButtonProps) => {
  if (isLink(props)) {
    const { classes, content, rest } = split(props);
    return (
      <Link {...rest} className={classes}>
        {content}
      </Link>
    );
  }
  if (isAnchor(props)) {
    const { classes, content, rest } = split(props);
    return (
      <a {...rest} className={classes}>
        {content}
      </a>
    );
  }
  const { classes, content, rest } = split(props);
  return (
    <button type="button" {...rest} className={classes}>
      {content}
    </button>
  );
};
